import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, Download, Maximize, Minimize2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";

const ImageViewerModal = ({ images, initialIndex = 0, open, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0, distance: 0 });
  const [fitMode, setFitMode] = useState(true); // True = fit to screen, False = allow overflow with scroll
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Update currentIndex when initialIndex prop changes
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  // Reset zoom and pan when changing images
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setFitMode(true); // Reset to fit mode on image change
  }, [currentIndex]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          handlePrevious();
          break;
        case "ArrowRight":
          e.preventDefault();
          handleNext();
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
        case "+":
        case "=":
          e.preventDefault();
          handleZoomIn();
          break;
        case "-":
          e.preventDefault();
          handleZoomOut();
          break;
        case "0":
          e.preventDefault();
          resetZoom();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, currentIndex, zoom]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.5, 5));
  };

  const handleZoomOut = () => {
    setZoom((prev) => {
      const newZoom = Math.max(prev - 0.5, 0.5);
      if (newZoom === 1) {
        setPan({ x: 0, y: 0 }); // Reset pan when back to normal
      }
      return newZoom;
    });
  };

  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleFitToScreen = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setFitMode(true);
  };

  const handleActualSize = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setFitMode(false); // Allow image to be shown at actual size with scroll
  };

  // Double-click to toggle between fit and actual size
  const handleDoubleClick = () => {
    if (fitMode) {
      // Switch to actual size mode
      setFitMode(false);
      setZoom(1);
      setPan({ x: 0, y: 0 });
    } else {
      // Switch back to fit mode
      setFitMode(true);
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }
  };

  // Mouse wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.2 : 0.2;
    setZoom((prev) => {
      const newZoom = Math.max(0.5, Math.min(5, prev + delta));
      if (newZoom !== 1) {
        setFitMode(false); // Exit fit mode when zooming
      }
      if (newZoom === 1) {
        setPan({ x: 0, y: 0 });
      }
      return newZoom;
    });
  };

  // Mouse drag to pan (when zoomed in or out)
  const handleMouseDown = (e) => {
    if (zoom !== 1) {  // Allow drag when zoomed in (>1) or out (<1)
      setIsDragging(true);
      setDragStart({
        x: e.clientX - pan.x,
        y: e.clientY - pan.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom !== 1) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch gestures for mobile
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      // Single touch - prepare for swipe
      setTouchStart({
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        distance: 0,
      });
    } else if (e.touches.length === 2) {
      // Two fingers - prepare for pinch zoom
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      setTouchStart({
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        distance: distance,
      });
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && touchStart.distance > 0) {
      // Pinch zoom
      e.preventDefault();
      const currentDistance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const scale = currentDistance / touchStart.distance;
      setZoom((prev) => Math.max(0.5, Math.min(5, prev * scale)));
      setTouchStart((prev) => ({ ...prev, distance: currentDistance }));
    }
  };

  const handleTouchEnd = (e) => {
    if (e.changedTouches.length === 1 && touchStart.distance === 0) {
      // Check for swipe gesture
      const deltaX = e.changedTouches[0].clientX - touchStart.x;
      const deltaY = Math.abs(e.changedTouches[0].clientY - touchStart.y);
      
      // Horizontal swipe (threshold: 50px, max vertical: 100px)
      if (Math.abs(deltaX) > 50 && deltaY < 100) {
        if (deltaX > 0) {
          handlePrevious(); // Swipe right
        } else {
          handleNext(); // Swipe left
        }
      }
    }
    setTouchStart({ x: 0, y: 0, distance: 0 });
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = images[currentIndex];
    link.download = `image-${currentIndex + 1}.jpg`;
    link.click();
  };

  if (!images || images.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-[100vw] max-h-[100vh] w-full h-full p-0 bg-black/95 border-0" 
        data-testid="image-viewer-modal"
      >
        <DialogTitle className="sr-only">Image Viewer</DialogTitle>
        <div 
          className="relative w-full h-full flex items-center justify-center"
          onClick={(e) => {
            // Close dialog when clicking on the background (not on image or controls)
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          {/* Close Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 z-50 text-white hover:bg-white/20 md:w-10 md:h-10 w-8 h-8"
            onClick={onClose}
            data-testid="modal-close-button"
          >
            <X className="md:w-6 md:h-6 w-5 h-5" />
          </Button>

          {/* Navigation Buttons */}
          {images.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 md:left-4 z-50 text-white hover:bg-white/20 md:w-12 md:h-12 w-10 h-10"
                onClick={handlePrevious}
                data-testid="modal-prev-button"
              >
                <ChevronLeft className="md:w-8 md:h-8 w-6 h-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 md:right-4 z-50 text-white hover:bg-white/20 md:w-12 md:h-12 w-10 h-10"
                onClick={handleNext}
                data-testid="modal-next-button"
              >
                <ChevronRight className="md:w-8 md:h-8 w-6 h-6" />
              </Button>
            </>
          )}

          {/* Zoom Controls */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-50 flex gap-2 bg-black/70 rounded-full p-2">
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 md:w-10 md:h-10 w-8 h-8"
              onClick={handleZoomOut}
              data-testid="zoom-out-button"
              disabled={zoom <= 0.5}
            >
              <ZoomOut className="md:w-5 md:h-5 w-4 h-4" />
            </Button>
            <span className="text-white px-3 flex items-center text-xs md:text-sm min-w-[60px] justify-center">
              {Math.round(zoom * 100)}%
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 md:w-10 md:h-10 w-8 h-8"
              onClick={handleZoomIn}
              data-testid="zoom-in-button"
              disabled={zoom >= 5}
            >
              <ZoomIn className="md:w-5 md:h-5 w-4 h-4" />
            </Button>
            <div className="w-px bg-white/30 mx-1"></div>
            <Button
              variant="ghost"
              size="icon"
              className={`text-white hover:bg-white/20 md:w-10 md:h-10 w-8 h-8 ${fitMode ? 'bg-white/20' : ''}`}
              onClick={handleFitToScreen}
              data-testid="fit-screen-button"
              title="Fit to screen (Press 0)"
            >
              <Maximize className="md:w-5 md:h-5 w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={`text-white hover:bg-white/20 md:w-10 md:h-10 w-8 h-8 ${!fitMode && zoom === 1 ? 'bg-white/20' : ''}`}
              onClick={handleActualSize}
              data-testid="actual-size-button"
              title="Actual size with scroll"
            >
              <Minimize2 className="md:w-5 md:h-5 w-4 h-4" />
            </Button>
            <div className="w-px bg-white/30 mx-1"></div>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 md:w-10 md:h-10 w-8 h-8"
              onClick={handleDownload}
              data-testid="download-button"
            >
              <Download className="md:w-5 md:h-5 w-4 h-4" />
            </Button>
          </div>

          {/* Image Counter */}
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 bg-black/70 text-white px-4 py-2 rounded-full text-xs md:text-sm">
            {currentIndex + 1} / {images.length}
          </div>

          {/* Help Text (Desktop only) */}
          <div className="hidden md:block absolute top-16 left-1/2 transform -translate-x-1/2 z-50 bg-black/50 text-white/70 px-3 py-1 rounded text-xs">
            Double-click: {fitMode ? 'Actual Size' : 'Fit Screen'} • Scroll: Zoom • Drag when zoomed: Pan • Arrows: Navigate
          </div>

          {/* Main Image Container */}
          <div 
            ref={containerRef}
            className={`absolute inset-0 flex items-center justify-center ${fitMode ? 'overflow-hidden' : 'overflow-auto'}`}
            onWheel={!fitMode && zoom === 1 ? null : handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={(e) => {
              // Close dialog when clicking on empty space (not on the image)
              if (e.target === e.currentTarget) {
                onClose();
              }
            }}
            style={{ 
              cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : (fitMode ? 'default' : 'default')
            }}
          >
            <img
              ref={imageRef}
              src={images[currentIndex]}
              alt={`Image ${currentIndex + 1}`}
              className={`select-none ${fitMode ? '' : 'w-auto h-auto'}`}
              style={
                fitMode || zoom !== 1
                  ? {
                      transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                      transition: isDragging ? 'none' : 'transform 0.3s ease',
                      maxWidth: fitMode ? '100vw' : 'none',
                      maxHeight: fitMode ? '100vh' : 'none',
                      width: fitMode ? 'auto' : 'auto',
                      height: fitMode ? 'auto' : 'auto',
                      objectFit: fitMode ? 'contain' : 'none',
                    }
                  : {
                      // In actual size mode without zoom, just show the image at its natural size
                      maxWidth: 'none',
                      maxHeight: 'none',
                    }
              }
              onDoubleClick={handleDoubleClick}
              data-testid="modal-image"
              draggable="false"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageViewerModal;